import React, { FC, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogActions, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import { Maybe, SortableScanModule } from '@/common/types';
import { FormTextField } from '@/components/common/Form/Form';
import { Button } from '@/components/common/button/button';
import { moduleImages } from './moduleImages';
import { Dialog, DialogContent, DialogTitle, ModuleFormFieldContainer, ModuleImageImg } from './styles';

export const scanModuleSchema = yup.object().shape({
    name: yup.string().required('Module Name is required'),
    subtitle: yup.string().required('Subtitle is required'),
    imageName: yup.string().default('Assessment'),
    isLocked: yup.boolean().required('Locked status is required'),
    imageUrl: yup.string().url('Invalid URL format').nullable(),
    assessments: yup.array().nullable().default([]),
});

export const ModuleModal: FC<{
    module?: SortableScanModule;
    open: boolean;
    onClose: () => void;
    onSave: (newModule: SortableScanModule, oldModuleName: Maybe<string>) => void;
    type: 'NEW' | 'EDIT';
}> = ({ open, onClose, onSave, module, type }) => {
    let existingImageKey = '1';
    Object.entries(moduleImages).forEach((moduleImageDefinition) => {
        const imageKey = moduleImageDefinition[0];
        const imageProps = moduleImageDefinition[1];
        if (imageProps.imageUrl === module?.imageUrl) {
            existingImageKey = imageKey;
        }
    });
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(scanModuleSchema),
        defaultValues: {
            name: module ? module.name : '',
            subtitle: module ? module.subtitle : '',
            imageName: module ? module.imageName : 'Assessment',
            isLocked: module ? module.isLocked : false,
            imageKey: existingImageKey,
            assessments: module ? module.assessments : [],
        },
    });

    useEffect(() => {
        if (module) {
            reset({
                name: module.name,
                subtitle: module.subtitle,
                imageKey: existingImageKey,
                imageName: module.imageName || 'Assessment',
                isLocked: module.isLocked,
                assessments: module.assessments,
            });
        }
    }, [existingImageKey, module, reset]);

    const save = (data) => {
        const newModule = {
            id: data.id || uuidv4(),
            name: data.name,
            subtitle: data.subtitle,
            imageName: data.imageName,
            isLocked: data.isLocked,
            imageUrl: moduleImages[data.imageKey].imageUrl,
            assessments: module ? module.assessments : [],
        };
        onSave(newModule, module && module.id);
        handleClose();
    };

    const handleClose = () => {
        onClose();
        reset();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{type === 'EDIT' ? 'Edit' : 'Add'} Module</DialogTitle>
            <DialogContent>
                <FormTextField
                    name="name"
                    control={control}
                    errors={errors}
                    autoFocus
                    margin="dense"
                    label="Name"
                    type="text"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />
                <FormTextField
                    name="subtitle"
                    control={control}
                    errors={errors}
                    margin="dense"
                    label="Subtitle"
                    type="text"
                    fullWidth
                    error={!!errors.subtitle}
                    helperText={errors.subtitle?.message}
                />
                <Controller
                    name="imageKey"
                    control={control}
                    render={({ field }) => {
                        return (
                            <FormControl variant="outlined">
                                <ModuleFormFieldContainer>
                                    <InputLabel shrink htmlFor="imageKey">
                                        Image Url
                                    </InputLabel>
                                    <Select label="Image Url" {...field}>
                                        {Object.entries(moduleImages).map((moduleImageDefinition) => {
                                            const imageKey = moduleImageDefinition[0];
                                            const imageProps = moduleImageDefinition[1];
                                            return (
                                                <MenuItem key={imageKey} value={imageKey}>
                                                    <ModuleImageImg height={20} src={imageProps.imageUrl} />{' '}
                                                    {imageProps.name}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    <p> </p>
                                </ModuleFormFieldContainer>
                            </FormControl>
                        );
                    }}
                />
                <FormTextField
                    name="imageName"
                    control={control}
                    errors={errors}
                    margin="dense"
                    label="Image Name (Required for Scan Versions < 2.13.1)"
                    type="text"
                    fullWidth
                    error={!!errors.imageName}
                    helperText={errors.imageName?.message}
                />
                <Controller
                    name="isLocked"
                    control={control}
                    render={({ field }) => (
                        <FormControlLabel
                            control={<Switch {...field} checked={field.value} />}
                            label="Locked Status"
                            labelPlacement="start"
                        />
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="tertiary">
                    Cancel
                </Button>
                <Button type="submit" variant="primary" onClick={handleSubmit(save)}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
